/**
=========================================================
* Trust Cargo - backups (restore points)
=========================================================

* A restore point is a full copy of the system's data at one moment: every
* order, container, customer, payment and user account. Before a round of
* big changes, save one; if anything goes wrong, put it back with one click.
*
* Putting one back replaces everything that was added or changed after it,
* so the click is guarded twice: the word RESTORE has to be typed, and the
* server saves the data as it is right now before it touches anything - the
* restore itself shows up in the list as a point of its own and can be undone.
*
* Unlike the rest of the dashboard's API these calls need the admin to be
* signed in; an old session without a token is told to sign in again.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";

import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import LinearProgress from "@mui/material/LinearProgress";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api/v2";

/** the word the server asks for before a restore */
export const CONFIRM_WORD = "RESTORE";

const authHeaders = () => ({
  Accept: "application/json",
  Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
});

/** What a failed call means for the person looking at the screen. */
const problemOf = (e, fallback) => {
  const status = e?.response?.status;
  if (status === 401)
    return {
      signIn: true,
      text: "انتهت جلسة الدخول. سجّل خروج وادخل من جديد عشان تستخدم النسخ الاحتياطية.",
    };
  if (status === 403) return { text: "النسخ الاحتياطية بس للأدمن." };
  if (status === 409) return { text: "في نسخة أو رجوع شغّال هلأ. استنى دقيقة وجرّب كمان مرة." };
  return { text: e?.response?.data?.message || fallback };
};

/**
 * Rows of one table in a restore point.
 *
 * The accounts database is the old system's, and it still has tables of its
 * own called orders, containers and customers - so a count is read from the
 * database named for it, never from whichever one happens to have a table
 * by that name first.
 */
export const countIn = (point, table, connection) => {
  const db = (point?.databases || []).find(
    (d) => (!connection || d.connection === connection) && d.tables && d.tables[table] !== undefined
  );
  return db ? db.tables[table] : null;
};

const sizeOf = (point) => {
  const bytes = (point?.databases || []).reduce((sum, d) => sum + (Number(d.bytes) || 0), 0);
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const nameOf = (point) => point.label || point.created_at;

/** the file each database is downloaded as, in words */
const DATABASE_NAMES = { v2: "بيانات الشحن", mysql: "الحسابات" };

const COUNTS = [
  { table: "orders", label: "طلبية", from: "v2" },
  { table: "containers", label: "حاوية", from: "v2" },
  { table: "customers", label: "زبون", from: "v2" },
  { table: "payments", label: "دفعة", from: "v2" },
  { table: "users", label: "مستخدم" },
];

/**
 * The theme only colours primary and secondary contained buttons, so a
 * warning one came out white. And the page is right-to-left while MUI's icon
 * spacing is left-to-right, which pushed the icon into the text.
 */
const RTL_ICON = { "& .MuiButton-startIcon": { marginLeft: "8px", marginRight: "-4px" } };

const RESTORE_BUTTON = ({ palette }) => ({
  ...RTL_ICON,
  backgroundColor: palette.warning.main,
  color: palette.dark.main,
  "&:hover, &:focus:not(:hover)": {
    backgroundColor: palette.warning.focus,
    color: palette.dark.main,
  },
});

function PointRow({ point, latest, busy, onRestore, onDownload, onDelete }) {
  const safety = point.kind === "safety";

  return (
    <SuiBox
      data-testid={`point-${point.id}`}
      sx={({ palette, borders, functions: { rgba } }) => ({
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        px: 2.5,
        py: 2,
        borderRadius: borders.borderRadius.md,
        border: `1px solid ${rgba(palette.dark.main, 0.1)}`,
        backgroundColor: latest ? rgba(palette.success.main, 0.06) : "transparent",
      })}
    >
      <Icon
        sx={({ palette }) => ({
          fontSize: "28px !important",
          color: safety ? palette.warning.main : palette.primary.main,
        })}
      >
        {safety ? "history" : "backup"}
      </Icon>

      <SuiBox sx={{ flex: "1 1 360px", minWidth: 0 }}>
        <SuiBox sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <SuiTypography sx={{ fontSize: 16, fontWeight: 700 }}>{nameOf(point)}</SuiTypography>
          {latest && (
            <Chip
              label="الأحدث"
              size="small"
              color="success"
              sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
            />
          )}
          {safety && (
            <Chip
              label="تلقائية قبل الرجوع"
              size="small"
              sx={({ palette, functions: { rgba } }) => ({
                height: 20,
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: rgba(palette.warning.main, 0.18),
                color: palette.warning.focus,
              })}
            />
          )}
        </SuiBox>

        <SuiTypography sx={{ fontSize: 13, opacity: 0.75, mt: 0.25 }} dir="ltr" component="div">
          <span style={{ unicodeBidi: "isolate" }}>{point.created_at}</span>
          {point.created_by ? ` · ${point.created_by}` : ""}
          {` · ${sizeOf(point)}`}
        </SuiTypography>

        <SuiBox sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 0.75 }}>
          {COUNTS.map(({ table, label, from }) => {
            const n = countIn(point, table, from);
            if (n === null) return null;
            return (
              <SuiTypography key={table} sx={{ fontSize: 12.5, opacity: 0.8 }}>
                <b>{n}</b> {label}
              </SuiTypography>
            );
          })}
        </SuiBox>
      </SuiBox>

      <SuiBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {(point.databases || []).map((d) => (
          <Tooltip key={d.connection} title={`تنزيل ${DATABASE_NAMES[d.connection] || d.database}`}>
            <span>
              <IconButton
                aria-label={`Download ${d.connection} of ${nameOf(point)}`}
                onClick={() => onDownload(point, d)}
                disabled={busy}
                size="small"
              >
                <Icon>{d.connection === "v2" ? "download" : "manage_accounts"}</Icon>
              </IconButton>
            </span>
          </Tooltip>
        ))}

        <Tooltip title="حذف النقطة">
          <span>
            <IconButton
              aria-label={`Delete ${nameOf(point)}`}
              onClick={() => onDelete(point)}
              disabled={busy}
              size="small"
            >
              <Icon>delete_outline</Icon>
            </IconButton>
          </span>
        </Tooltip>

        <Button
          variant="contained"
          color="warning"
          onClick={() => onRestore(point)}
          disabled={busy}
          startIcon={<Icon>settings_backup_restore</Icon>}
          sx={(theme) => ({ ...RESTORE_BUTTON(theme), height: 40, fontSize: 14, mr: 1 })}
        >
          رجوع لهاي النقطة
        </Button>
      </SuiBox>
    </SuiBox>
  );
}

PointRow.propTypes = {
  point: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    kind: PropTypes.string,
    created_at: PropTypes.string,
    created_by: PropTypes.string,
    databases: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  latest: PropTypes.bool,
  busy: PropTypes.bool,
  onRestore: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

PointRow.defaultProps = { latest: false, busy: false };

function Backups() {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [working, setWorking] = useState(""); // what is running, in words
  const [problem, setProblem] = useState(null);
  const [done, setDone] = useState("");

  const [restoring, setRestoring] = useState(null); // the point in the confirm window
  const [typed, setTyped] = useState("");

  const load = async () => {
    try {
      const res = await axios.get(`${API}/backups`, { headers: authHeaders() });
      setPoints(Array.isArray(res?.data?.points) ? res.data.points : []);
      // an error from the action that reloaded the list stays on screen;
      // every action clears the old one when it starts
    } catch (e) {
      setProblem(problemOf(e, "تعذّر تحميل النسخ الاحتياطية"));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setWorking("جاري حفظ نسخة من كل البيانات…");
    setDone("");
    setProblem(null);

    try {
      const res = await axios.post(
        `${API}/backups`,
        { label: label.trim() },
        { headers: { ...authHeaders(), "Content-Type": "application/json" } }
      );
      const point = res?.data?.point;
      setLabel("");
      setDone(
        `تم حفظ نقطة الاسترجاع «${point ? nameOf(point) : ""}». لو صار أي اشي بترجع لها من هون.`
      );
      await load();
    } catch (e) {
      setProblem(problemOf(e, "تعذّر حفظ النسخة"));
    }
    setWorking("");
  };

  const restore = async () => {
    const point = restoring;
    setRestoring(null);
    setTyped("");
    setWorking(`جاري الرجوع إلى «${nameOf(point)}»… لا تسكّر الصفحة.`);
    setDone("");
    setProblem(null);

    try {
      const res = await axios.post(
        `${API}/backups/${point.id}/restore`,
        { confirm: CONFIRM_WORD },
        { headers: { ...authHeaders(), "Content-Type": "application/json" } }
      );
      const safety = res?.data?.safety;
      const undo = safety
        ? `الوضع اللي كان قبل الرجوع انحفظ كنقطة «${nameOf(
            safety
          )}» - إذا بدك تلغي الرجوع ارجع لها. `
        : "";
      setDone(
        `رجعت البيانات إلى «${nameOf(
          point
        )}». ${undo}حدّث أي صفحة ثانية مفتوحة عشان تشوف البيانات الراجعة.`
      );
    } catch (e) {
      setProblem(problemOf(e, "تعذّر الرجوع"));
    }

    await load();
    setWorking("");
  };

  const download = async (point, database) => {
    setProblem(null);
    try {
      const res = await axios.get(`${API}/backups/${point.id}/download/${database.connection}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${database.database}-${point.id}.sql.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setProblem(problemOf(e, "تعذّر التنزيل"));
    }
  };

  const remove = async (point) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`حذف نقطة الاسترجاع «${nameOf(point)}»؟ ما بتقدر ترجع لها بعد الحذف.`))
      return;

    setProblem(null);
    try {
      await axios.delete(`${API}/backups/${point.id}`, { headers: authHeaders() });
      await load();
    } catch (e) {
      setProblem(problemOf(e, "تعذّر الحذف"));
    }
  };

  const busy = Boolean(working);
  const latestId = points.find((p) => p.kind !== "safety")?.id;

  return (
    <DashboardLayout>
      <SuiBox py={1} dir="rtl" sx={{ textAlign: "right" }}>
        <SuiBox mb={3}>
          <Card>
            <SuiBox
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
                px: 3,
                py: 2.5,
              }}
            >
              <SuiBox sx={{ flex: "1 1 280px", minWidth: 0 }}>
                <SuiTypography variant="h6">النسخ الاحتياطية</SuiTypography>
                <SuiTypography sx={{ fontSize: 13, opacity: 0.7 }}>
                  احفظ نسخة من كل البيانات قبل التعديلات الكبيرة، وارجع لها بكبسة زر
                </SuiTypography>
              </SuiBox>

              <TextField
                label="اسم النقطة (اختياري)"
                placeholder="مثلاً: قبل تعديل الأسعار"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                inputProps={{ maxLength: 120 }}
                InputLabelProps={{ shrink: true }}
                disabled={busy}
                sx={{
                  width: 280,
                  "& .MuiInputBase-root": { minHeight: 44, backgroundColor: "#fff" },
                }}
              />

              <Button
                variant="contained"
                color="primary"
                onClick={save}
                disabled={busy || Boolean(problem?.signIn)}
                startIcon={<Icon>backup</Icon>}
                sx={{ ...RTL_ICON, height: 44, fontSize: 14 }}
              >
                حفظ نقطة استرجاع الآن
              </Button>
            </SuiBox>

            <SuiBox px={3} pb={2}>
              <Alert severity="info" icon={<Icon>info</Icon>}>
                النسخة فيها كل الطلبيات والحاويات والزبائن والدفعات والموردين وحسابات المستخدمين.
                الرجوع لنقطة بيرجّع كل هاد لتلك اللحظة، وأي اشي انضاف أو تعدّل بعدها بيروح - بس قبل
                أي رجوع بينحفظ الوضع الحالي تلقائياً، فالرجوع نفسه بينلغى.
              </Alert>
            </SuiBox>

            {working && (
              <SuiBox px={3} pb={2}>
                <Alert severity="warning" icon={<Icon>hourglass_top</Icon>}>
                  {working}
                </Alert>
                <LinearProgress sx={{ mt: 1 }} />
              </SuiBox>
            )}

            {problem && (
              <SuiBox px={3} pb={2}>
                <Alert
                  severity="error"
                  action={
                    problem.signIn ? (
                      <Button
                        color="inherit"
                        size="small"
                        onClick={() => {
                          sessionStorage.clear();
                          window.location.href = "/authentication/sign-in";
                        }}
                      >
                        تسجيل الدخول
                      </Button>
                    ) : null
                  }
                >
                  {problem.text}
                </Alert>
              </SuiBox>
            )}

            {done && (
              <SuiBox px={3} pb={2}>
                <Alert severity="success">{done}</Alert>
              </SuiBox>
            )}

            {loading ? (
              <CargoLoader label="تحميل النسخ الاحتياطية" />
            ) : (
              <SuiBox px={3} pb={3} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {points.length === 0 && !problem && (
                  <SuiTypography sx={{ fontSize: 14, opacity: 0.7, py: 2, textAlign: "center" }}>
                    ما في نقاط استرجاع لسا. احفظ أول وحدة قبل ما تبلّش التعديلات.
                  </SuiTypography>
                )}

                {points.map((p) => (
                  <PointRow
                    key={p.id}
                    point={p}
                    latest={p.id === latestId}
                    busy={busy}
                    onRestore={(point) => {
                      setTyped("");
                      setRestoring(point);
                    }}
                    onDownload={download}
                    onDelete={remove}
                  />
                ))}
              </SuiBox>
            )}
          </Card>
        </SuiBox>
      </SuiBox>

      <Dialog
        open={Boolean(restoring)}
        onClose={() => setRestoring(null)}
        dir="rtl"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "right" }}>
          الرجوع إلى «{restoring ? nameOf(restoring) : ""}»
        </DialogTitle>
        <DialogContent sx={{ textAlign: "right" }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            كل البيانات بترجع لتاريخ {restoring?.created_at}. أي طلبية أو حاوية أو زبون أو دفعة أو
            تعديل صار بعد هاد الوقت بيروح. الوضع الحالي بينحفظ تلقائياً قبل الرجوع.
          </Alert>
          <SuiTypography sx={{ fontSize: 14, mb: 1 }}>
            للتأكيد اكتب <b dir="ltr">{CONFIRM_WORD}</b>
          </SuiTypography>
          <TextField
            autoFocus
            fullWidth
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            inputProps={{ dir: "ltr", "aria-label": "Type RESTORE to confirm" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoring(null)}>إلغاء</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={restore}
            disabled={typed !== CONFIRM_WORD}
            startIcon={<Icon>settings_backup_restore</Icon>}
            sx={RESTORE_BUTTON}
          >
            رجوع
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Backups;
